/**
 * @fileoverview Value Object TokenInfo - Encapsula información de tokens
 * Implementa patrón Value Object con inmutabilidad garantizada
 */

/**
 * @class TokenInfo
 * @description Value Object que representa información de tokens de autenticación
 * con validación de formato y expiración
 */
export default class TokenInfo {
  /**
   * @constructor
   * @param {Object} props - Propiedades del token
   * @param {string} props.token - Token JWT o string
   * @param {number|Date} props.expiresAt - Timestamp o Date de expiración
   * @param {string} [props.refreshToken] - Token de refresco (opcional)
   * @param {string} [props.type='Bearer'] - Tipo de token
   */
  constructor({
    token,
    expiresAt,
    refreshToken = null,
    type = 'Bearer'
  }) {
    this._validateProps({ token, expiresAt, type });
    
    this._token = token;
    this._expiresAt = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
    this._refreshToken = refreshToken;
    this._type = type;
    
    // Congelar la instancia para garantizar inmutabilidad
    Object.freeze(this);
  }
  
  /**
   * Validación de propiedades
   * @private
   * @param {Object} props - Propiedades a validar
   * @throws {Error} Si alguna validación falla
   */
  _validateProps({ token, expiresAt, type }) {
    if (!token || typeof token !== 'string') {
      throw new Error('Token must be a non-empty string');
    }
    
    // Validar que expiresAt sea convertible a Date
    try {
      new Date(expiresAt);
    } catch (e) {
      throw new Error('ExpiresAt must be a valid Date or timestamp');
    }
    
    if (!['Bearer', 'Basic', 'OAuth'].includes(type)) {
      throw new Error('Token type must be one of: Bearer, Basic, OAuth');
    }
  }
  
  // Getters - No hay setters para garantizar inmutabilidad
  get token() { return this._token; }
  get expiresAt() { return new Date(this._expiresAt); } // Devuelve copia para evitar modificaciones
  get refreshToken() { return this._refreshToken; }
  get type() { return this._type; }
  
  /**
   * Verifica si el token ha expirado
   * @param {number} [gracePeriodMinutes=0] - Periodo de gracia en minutos
   * @returns {boolean} True si el token ha expirado
   */
  isExpired(gracePeriodMinutes = 0) {
    const now = new Date();
    const graceMs = gracePeriodMinutes * 60 * 1000;
    return this._expiresAt.getTime() <= (now.getTime() + graceMs);
  }
  
  /**
   * Calcula el tiempo restante en segundos
   * @returns {number} Segundos restantes hasta expiración (0 si ya expiró)
   */
  getRemainingSeconds() {
    const now = new Date();
    const remainingMs = this._expiresAt.getTime() - now.getTime();
    return Math.max(0, Math.floor(remainingMs / 1000));
  }
  
  /**
   * Crea una nueva instancia con propiedades actualizadas
   * @param {Object} props - Propiedades que se desean actualizar
   * @returns {TokenInfo} Nueva instancia con propiedades actualizadas
   */
  with(props) {
    return new TokenInfo({
      token: this._token,
      expiresAt: this._expiresAt,
      refreshToken: this._refreshToken,
      type: this._type,
      ...props
    });
  }
  
  /**
   * Formatea el token para autorización HTTP
   * @returns {string} Header de autorización formateado
   */
  toAuthHeader() {
    return `${this._type} ${this._token}`;
  }
  
  /**
   * Convierte la instancia a un objeto plano para serialización
   * @returns {Object} Representación plana de TokenInfo
   */
  toJSON() {
    return {
      token: this._token,
      expiresAt: this._expiresAt.toISOString(),
      refreshToken: this._refreshToken,
      type: this._type,
      isExpired: this.isExpired(),
      remainingSeconds: this.getRemainingSeconds()
    };
  }
}

// Exportado con ES Modules (export default) al inicio de la clase
